import { ChangeDetectorRef, Component, computed, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ListsService } from '../../services/lists.service';
import { SelectedComponent } from "../selected/selected.component";
import { InputComponent } from "../input/input.component";
import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import { CommonModule } from '@angular/common';
import { INDEPMENT_INPUTS, INDEPMENT_SELECTORS } from 'app/config/constants.config';
import {
  buildPreloadedConfig,
  buildPreloadedSelectConfig,
  normalizeFieldValue,
  mergeSelectOptionsWithPreload,
  resolveOptions,
  resolveRawFieldValue,
} from '../utils/field-preload.utils';

@Component({
  selector: 'app-independent',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    SelectedComponent,
    InputComponent,
    AutocompleteComponent,
    CommonModule
  ],
  templateUrl: './independent.component.html'
})
export class IndependentComponent implements OnInit, OnChanges, OnDestroy {

  form: FormGroup;
  dataInputs = INDEPMENT_INPUTS;
  dataSelected = INDEPMENT_SELECTORS;

  @Output() formSubmit: EventEmitter<any> = new EventEmitter<any>();
  @Input() defaultData: any;
  @Output() validData = new EventEmitter<any>();
  @Input() isCheckData = false;
  @Input() dataLastOnboarding: any;
  @Input() isLightVersion = false;
  @Input() resetTimer = false;

  codpros: any[] = [];
  private isUpdating = false;
  private subscription: any;

  lightDataInputs = computed(() => {
    if (!this.isLightVersion) return this.dataInputs();
    return this.dataInputs().filter((input: any) =>
      input.formName === 'income'
    );
  });

  lightDataSelected = computed(() => {
    if (!this.isLightVersion) return this.dataSelected();
    return [];
  });

  constructor(
    public _listService: ListsService,
    private cdr: ChangeDetectorRef
  ) {
    this.form = new FormGroup({
      indact: new FormControl('', [Validators.required]),
      academic: new FormControl('', [Validators.required]),
      specialProtecionProgram: new FormControl('', [Validators.required]),
      econmicActivity: new FormControl('', [Validators.required]),
      income: new FormControl('', [Validators.required]),
      codpro: new FormControl('', [Validators.required]),
      incomeExtraActivity: new FormControl('', [Validators.required]),
    });
  }

  ngOnInit(): void {
    this.getAllIndAct();
    this.getAllAcademicLevels();
    this.getAllSpecialProgram();
    this.getAllProfessions();
    this.updateFormData();

    this.subscription = this.form.valueChanges.subscribe((value) => {
      if (!this.isUpdating) {
        setTimeout(() => {
          this.emitFormData(value);
        }, 50);
      }
    });
  }

  private preloadSnapshot = '';

  private getPreloadKey(data: any): string {
    return JSON.stringify({
      income: data.income,
      incomeExtraActivity: data.incomeExtraActivity,
      indact: data.indact,
      academic: resolveRawFieldValue(data, 'academic'),
      specialProtecionProgram: resolveRawFieldValue(data, 'specialProtecionProgram'),
      econmicActivity: data.econmicActivity,
      codpro: data.codpro,
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['defaultData']?.currentValue) return;

    const newData = changes['defaultData'].currentValue;
    if (!newData || !this.hasAddressData(newData)) return;

    const key = this.getPreloadKey(newData);
    if (key === this.preloadSnapshot) return;

    const isFirstPopulation = this.preloadSnapshot === '';
    this.preloadSnapshot = key;

    if (isFirstPopulation) {
      this.updateFormData();
    }
  }

  private hasAddressData(data: any): boolean {
    return data?.asotip != null && data?.asotip !== '';
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private emitFormData(value: any) {
    const formValid = this.form.valid;
    this.formSubmit.emit({
      ...value,
      _isValid: formValid,
      _formName: 'independent'
    });
  }

  private updateFormData(): void {
    this.isUpdating = true;
    const newData = this.defaultData;

    if (newData && Object.keys(newData).length > 0) {
      this.form.patchValue({
        income: normalizeFieldValue(newData.income, 'number'),
        incomeExtraActivity: normalizeFieldValue(newData.incomeExtraActivity, 'number'),
        indact: newData.indact ?? '',
        academic: resolveRawFieldValue(newData, 'academic') ?? '',
        specialProtecionProgram: resolveRawFieldValue(newData, 'specialProtecionProgram') ?? '',
        econmicActivity: newData.econmicActivity ?? '',
        codpro: newData.codpro ?? '',
      }, { emitEvent: false });

      this.dataInputs.update((inputs: any) =>
        inputs.map((input: any) => buildPreloadedConfig(input, newData[input.formName], input.type))
      );

      this.dataSelected.update((selects: any) =>
        selects.map((select: any) =>
          buildPreloadedSelectConfig(select, newData[select.formName], select.options, newData)
        )
      );
    }

    setTimeout(() => {
      this.isUpdating = false;
    }, 200);
  }

  private refreshSelectConfigs(): void {
    const newData = this.defaultData;
    if (!newData || Object.keys(newData).length === 0) return;

    this.dataSelected.update((selects: any) =>
      selects.map((select: any) => {
        const currentOptions = resolveOptions(select.options);
        const mergedOptions = mergeSelectOptionsWithPreload(currentOptions, newData, select.formName);
        if (mergedOptions.length !== currentOptions.length && select.options?.set) {
          select.options.set(mergedOptions);
        }
        return buildPreloadedSelectConfig(select, newData[select.formName], select.options, newData);
      })
    );
  }

  getAllProfessions() {
    this._listService.getCodpro().subscribe({
      next: (response: any) => { this.codpros = response; },
      error: (error: any) => { console.error('Error cargando profesiones:', error); }
    });
  }

  getAllIndAct() {
    this._listService.getIndAct().subscribe({
      next: (response: any) => {
        this.dataSelected().at(0)?.options.set(response);
        this.refreshSelectConfigs();
      },
      error: (error: any) => { console.error('Error loading independent activities:', error); }
    });
  }

  getAllSpecialProgram() {
    this._listService.getAllSpecialProgram().subscribe({
      next: (response: any) => {
        const merged = mergeSelectOptionsWithPreload(
          response,
          this.defaultData,
          'specialProtecionProgram'
        );
        this.dataSelected().at(2)?.options.set(merged);
        this.refreshSelectConfigs();
      },
      error: (error: any) => { console.error('Error loading special programs:', error); }
    });
  }

  getAllAcademicLevels() {
    this._listService.getAllAcademicLevels().subscribe({
      next: (response: any) => {
        this.dataSelected().at(1)?.options.set(response);
        this.refreshSelectConfigs();
      },
      error: (error: any) => { console.error('Error loading academic levels:', error); }
    });
  }

  getOptionLabel(fieldName: string, value: any): string {
    if (!value && value !== 0) return 'No especificado';
    const selectConfig = this.dataSelected().find(config => config.formName === fieldName);
    if (selectConfig && selectConfig.options) {
      const option = selectConfig.options().find((opt: any) => opt.value == value);
      return option ? option.label : value.toString();
    }
    return value.toString();
  }

  getEconomicActivityLabel(value: any): string {
    if (!value) return 'No especificado';
    let economicActivity: any;
    this._listService.findByIdEconomicActivity(value).subscribe({
      next: (response: any) => { economicActivity = response?.data?.name; },
      error: (error: any) => {}
    });
    return economicActivity?.toString() || value.toString();
  }

  getProfessionLabel(value: any): string {
    if (!value) return 'No especificado';
    const profession = this.codpros.find(p => p.value == value);
    return profession ? profession.label : value.toString();
  }

  setFieldEnabled(event: { formName: string; enabled: boolean }): void {
    this.dataInputs.update((inputs: any) =>
      inputs.map((input: any) =>
        input.formName === event.formName ? { ...input, enabled: event.enabled } : input
      )
    );

    this.dataSelected.update((selects: any) =>
      selects.map((select: any) =>
        select.formName === event.formName ? { ...select, enabled: event.enabled } : select
      )
    );
  }

  setCodpro(event: { value: string | number | null } | string | number | null) {
    const codproId = event && typeof event === 'object' && 'value' in event
      ? event.value
      : event;

    this.isUpdating = true;
    this.form.patchValue({ codpro: codproId }, { emitEvent: false });
    this.formSubmit.emit({
      ...this.form.value,
      _isValid: this.form.valid,
      _formName: 'independent'
    });
    setTimeout(() => {
      this.isUpdating = false;
    }, 100);
  }

  handleValue(event: { formName: string, value: string | number, isValid?: boolean, isDirty?: boolean }) {
    const control = this.form.get(event.formName);
    if (control?.value == event.value) return;

    this.isUpdating = true;
    control?.setValue(event.value, { emitEvent: false });

    const formValue = this.form.value;
    this.formSubmit.emit({
      ...formValue,
      _isValid: this.form.valid,
      _field: event.formName,
      _isDirty: event.isDirty || false
    });

    setTimeout(() => {
      this.isUpdating = false;
    }, 100);
  }

  handleAutocompleteValue(event: { value: string | number | null } | string | number | null) {
    const activityId = event && typeof event === 'object' && 'value' in event
      ? event.value
      : event;

    this.isUpdating = true;
    this.form.patchValue({ econmicActivity: activityId ?? '' }, { emitEvent: false });
    this.formSubmit.emit({
      ...this.form.value,
      _isValid: this.form.valid,
      _formName: 'independent',
      _isDirty: event && typeof event === 'object' && 'isDirty' in event
        ? Boolean((event as { isDirty?: boolean }).isDirty)
        : true
    });
    setTimeout(() => {
      this.isUpdating = false;
    }, 100);
  }
}
